import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import PaymentReceiptDialog from '@/components/PaymentReceiptDialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, MagnifyingGlass, BookOpen, PencilSimple, Trash, Receipt as ReceiptIcon, FilePlus, Copy, Check } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function CustomersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = useCan('customer', 'edit');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Payment receipt edit state (opened from the pencil next to each payment row).
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const canDelete = useCan('customer', 'delete');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', address: '', notes: '', group_id: '', advance_balance: '', credentials: [] });
  // Customer-group master — used by both the table "Group" column and the
  // add/edit dialog's Group dropdown. `newGroupName` drives the inline-create
  // shortcut inside the dialog so the user never has to leave for the
  // Masters page.
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const groupName = (gid) => groups.find(g => g.id === gid)?.name || '';
  const [credForm, setCredForm] = useState({ site: '', user_id: '' });
  const [editCredIdx, setEditCredIdx] = useState(-1);
  const [ledger, setLedger] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [finAccounts, setFinAccounts] = useState([]);
  const [showPayDue, setShowPayDue] = useState(null);
  const [duePayAmount, setDuePayAmount] = useState('');
  const [duePayMode, setDuePayMode] = useState('');
  const [duePayDate, setDuePayDate] = useState(new Date().toISOString().split('T')[0]);
  // Advance receive dialog state
  const [showAdvance, setShowAdvance] = useState(null); // null | customer
  const [advForm, setAdvForm] = useState({ amount: '', payment_mode: '', date: new Date().toISOString().split('T')[0], notes: '' });
  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(null); // null | customer

  const fetchCustomers = () => {
    api.get('/customers').then(r => setCustomers(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCustomers();
    api.get('/finance/accounts').then(r => setFinAccounts(r.data)).catch(console.error);
    api.get('/customer-groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, []);

  // Auto-open the ledger dialog when redirected back from CreateInvoicePage /
  // ReceiptPage with `?openLedger=<customer_id>`. Clears the param afterwards
  // so a manual refresh on /customers doesn't keep re-opening it.
  useEffect(() => {
    const id = searchParams.get('openLedger');
    if (!id || customers.length === 0) return;
    const cust = customers.find(c => c.id === id);
    if (cust) {
      setLedger(cust);
      api.get(`/customers/${id}/ledger`).then(r => setLedgerData(r.data)).catch(() => setLedgerData([]));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('openLedger');
    setSearchParams(next, { replace: true });
  }, [customers, searchParams, setSearchParams]);

  const handleAdd = async (force = false) => {
    if (!form.name || !form.phone) { toast.error('Name and phone required'); return; }
    // Build payload — strip the empty-string advance_balance (treat it as "no override").
    const payload = {
      name: form.name,
      phone: form.phone,
      address: form.address,
      notes: form.notes,
      group_id: form.group_id || '',
      credentials: form.credentials || [],
    };
    if (editCustomer && isAdmin && form.advance_balance !== '' && form.advance_balance !== null && !isNaN(parseFloat(form.advance_balance))) {
      const next = parseFloat(form.advance_balance);
      if (next < 0) { toast.error('Advance balance cannot be negative'); return; }
      payload.advance_balance = next;
    }
    try {
      if (editCustomer) {
        await api.put(`/customers/${editCustomer.id}${force ? '?force=true' : ''}`, payload);
        toast.success('Customer updated');
      } else {
        await api.post(`/customers${force ? '?force=true' : ''}`, payload);
        toast.success('Customer added');
      }
      setShowAdd(false);
      setEditCustomer(null);
      setForm({ name: '', phone: '', address: '', notes: '', group_id: '', advance_balance: '', credentials: [] });
      setCredForm({ site: '', user_id: '' });
      setEditCredIdx(-1);
      fetchCustomers();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(err.response.data.detail)) {
          handleAdd(true);
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save customer');
      }
    }
  };

  const handleEdit = (c) => {
    setEditCustomer(c);
    setForm({
      name: c.name,
      phone: c.phone,
      address: c.address || '',
      notes: c.notes || '',
      group_id: c.group_id || '',
      advance_balance: String(c.advance_balance || 0),
      credentials: (c.credentials || []).map(x => ({ site: x.site, user_id: x.user_id, modified_at: x.modified_at })),
    });
    setCredForm({ site: '', user_id: '' });
    setEditCredIdx(-1);
    setShowAdd(true);
  };

  const addOrUpdateCred = () => {
    const site = (credForm.site || '').trim();
    const uid = (credForm.user_id || '').trim();
    if (!site || !uid) { toast.error('Site name and user id required'); return; }
    setForm(f => {
      const arr = f.credentials || [];
      if (editCredIdx >= 0) {
        return { ...f, credentials: arr.map((c, i) => i === editCredIdx ? { ...c, site, user_id: uid } : c) };
      }
      return { ...f, credentials: [...arr, { site, user_id: uid }] };
    });
    setCredForm({ site: '', user_id: '' });
    setEditCredIdx(-1);
  };

  const editCred = (i) => {
    const c = form.credentials[i];
    setCredForm({ site: c.site, user_id: c.user_id });
    setEditCredIdx(i);
  };

  const removeCred = (i) => {
    setForm(f => ({ ...f, credentials: (f.credentials || []).filter((_, k) => k !== i) }));
    if (editCredIdx === i) { setCredForm({ site: '', user_id: '' }); setEditCredIdx(-1); }
  };

  const copyCred = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const openLedger = async (customer) => {
    setLedger(customer);
    const { data } = await api.get(`/customers/${customer.id}/ledger`);
    setLedgerData(data);
  };

  const handleReceiveDue = async () => {
    if (!showPayDue || !duePayAmount || parseFloat(duePayAmount) <= 0 || !duePayMode) { toast.error('Fill all fields'); return; }
    try {
      await api.put(`/invoices/${showPayDue.invoice_id}/payment`, {
        amount: parseFloat(duePayAmount), payment_mode: duePayMode, payment_date: duePayDate
      });
      toast.success('Payment recorded');
      setShowPayDue(null);
      setDuePayAmount('');
      setDuePayDate(new Date().toISOString().split('T')[0]);
      // Re-fetch ledger and customers
      if (ledger) {
        const { data } = await api.get(`/customers/${ledger.id}/ledger`);
        setLedgerData(data);
      }
      fetchCustomers();
    } catch { toast.error('Payment failed'); }
  };

  const handleDeleteCustomer = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/customers/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} deleted`);
      setConfirmDelete(null);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
      setConfirmDelete(null);
    }
  };

  const handleReceiveAdvance = async () => {    const amt = parseFloat(advForm.amount);
    if (!showAdvance || !amt || amt <= 0 || !advForm.payment_mode) { toast.error('Fill amount and payment mode'); return; }
    try {
      await api.post(`/customers/${showAdvance.id}/advance`, {
        amount: amt, payment_mode: advForm.payment_mode, date: advForm.date, notes: advForm.notes,
      });
      toast.success(`Advance of ${fmt(amt)} received from ${showAdvance.name}`);
      setShowAdvance(null);
      setAdvForm({ amount: '', payment_mode: '', date: new Date().toISOString().split('T')[0], notes: '' });
      // Refresh ledger if open for this customer
      if (ledger && ledger.id === showAdvance.id) {
        const { data } = await api.get(`/customers/${ledger.id}/ledger`);
        setLedgerData(data);
      }
      fetchCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record advance'); }
  };

  const ledgerTotalDue = ledgerData.length > 0 ? (ledgerData.reduce((s, e) => s + (e.debit || 0), 0) - ledgerData.reduce((s, e) => s + (e.credit || 0), 0)) : 0;
  const ledgerAdvanceHeld = ledgerData.length > 0 ? ledgerData.reduce((s, e) => s + (e.advance_in || 0) - (e.advance_out || 0), 0) : 0;

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search)
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-end">
        {canEdit && (
          <Button onClick={() => { setEditCustomer(null); setForm({ name: '', phone: '', address: '', notes: '', group_id: '', advance_balance: '', credentials: [] }); setCredForm({ site: '', user_id: '' }); setEditCredIdx(-1); setShowAdd(true); }} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="add-customer-btn">
            <Plus size={16} className="mr-2" /> Add Customer
          </Button>
        )}
      </div>

      <div className="relative w-64">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or phone"
          className="pl-9 rounded-sm border-[#E4E4E7]" data-testid="customer-search" />
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        <CardContent className="p-0">
          {loading ? <p className="p-4 text-sm text-[#52525B]">Loading...</p> : filtered.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#52525B]">No customers found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Phone</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Group</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Address</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Notes</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Total Billed</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Received</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Due</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Advance</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id} className="border-[#E4E4E7]">
                    <TableCell className="text-sm font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm text-[#52525B]">{c.phone}</TableCell>
                    <TableCell className="text-sm">
                      {c.group_id ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]">{groupName(c.group_id) || '—'}</span>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#52525B] max-w-[240px] truncate" title={c.address || ''}>{c.address || '-'}</TableCell>
                    <TableCell className="text-sm text-[#52525B] max-w-[240px] truncate" title={c.notes || ''}>{c.notes || '-'}</TableCell>
                    <TableCell className="text-right">
                      {(c.total_billed || 0) > 0 ? (
                        <span className="text-sm font-mono text-[#0A0A0A]" title={`${c.invoice_count || 0} invoice${c.invoice_count === 1 ? '' : 's'}`}>{fmt(c.total_billed)}</span>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {(c.total_received || 0) > 0 ? (
                        <span className="text-sm font-mono text-[#10B981]">{fmt(c.total_received)}</span>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {(c.total_due || 0) > 0 ? (
                        <span className="text-sm font-mono text-[#EA580C]">{fmt(c.total_due)}</span>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {(c.advance_balance || 0) > 0 ? (
                        <span className="text-sm font-mono font-semibold text-[#3B82F6]">{fmt(c.advance_balance)}</span>
                      ) : (
                        <span className="text-xs text-[#A1A1AA]">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(c)} data-testid={`edit-customer-${c.id}`}>
                            <PencilSimple size={14} />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openLedger(c)} data-testid={`ledger-${c.id}`}>
                          <BookOpen size={14} className="mr-1" /> Ledger
                        </Button>
                        {/* "+ Advance" entry point removed — use the dedicated Receipt page in
                            the sidebar (which can also adjust against open invoices). */}
                        {canDelete && ((c.outstanding_balance || 0) === 0 && (c.advance_balance || 0) === 0) && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#EF4444] hover:bg-[#FEF2F2]"
                            onClick={() => setConfirmDelete(c)}
                            title="Delete customer"
                            data-testid={`delete-customer-${c.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={showAdd} onOpenChange={(v) => { setShowAdd(v); if (!v) setEditCustomer(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
            <DialogDescription>Enter customer details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Mobile *</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1" data-testid="customer-phone-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="customer-name-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="rounded-sm mt-1" data-testid="customer-address-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Group (optional)</Label>
                {!showNewGroup && (
                  <button type="button" onClick={() => setShowNewGroup(true)} className="text-[10px] text-[#3B82F6] hover:text-[#2563EB] font-semibold" data-testid="customer-add-group-btn">+ New Group</button>
                )}
              </div>
              {showNewGroup ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    placeholder="New group name..."
                    className="rounded-sm h-9 text-sm"
                    data-testid="customer-new-group-input"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      const n = newGroupName.trim();
                      if (!n) { toast.error('Group name required'); return; }
                      try {
                        const { data } = await api.post('/customer-groups', { name: n });
                        setGroups(gs => [...gs, data]);
                        setForm(f => ({ ...f, group_id: data.id }));
                        setShowNewGroup(false);
                        setNewGroupName('');
                        toast.success(`Group "${data.name}" created`);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add group'); }
                    }}
                    className="h-9 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB]"
                    data-testid="customer-save-group-btn"
                  >Save</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => { setShowNewGroup(false); setNewGroupName(''); }} className="h-9 px-3 text-xs">Cancel</Button>
                </div>
              ) : (
                <Select value={form.group_id || '__none__'} onValueChange={v => setForm({ ...form, group_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="customer-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ungrouped</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#52525B]">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anything to remember about this customer..." className="rounded-sm mt-1 resize-none" data-testid="customer-notes-input" />
            </div>

            {/* Saved Login IDs — Site / User-ID / Last Modified table with
                Add + Edit + Copy + Delete actions per row. No passwords are
                ever stored, only the login id. */}
            <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3" data-testid="customer-credentials-section">
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B] mb-2">Saved Login IDs</p>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Site Name</Label>
                  <Input value={credForm.site} onChange={e => setCredForm({ ...credForm, site: e.target.value })} placeholder="e.g. Aadhar Portal" className="rounded-sm mt-1 h-9" data-testid="cred-site-input" />
                </div>
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">User ID</Label>
                  <Input value={credForm.user_id} onChange={e => setCredForm({ ...credForm, user_id: e.target.value })} placeholder="e.g. 1234-5678-9012" className="rounded-sm mt-1 h-9" data-testid="cred-user-id-input" />
                </div>
                <Button type="button" onClick={addOrUpdateCred}
                  className="rounded-sm h-9 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white"
                  data-testid="cred-add-btn">
                  {editCredIdx >= 0 ? <><Check size={14} className="mr-1" /> Update</> : <><Plus size={14} className="mr-1" /> Add</>}
                </Button>
              </div>
              {(form.credentials || []).length > 0 && (
                <div className="mt-3 border-t border-[#E4E4E7] pt-2">
                  <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA] px-2 pb-1">
                    <span>Site</span>
                    <span>User ID</span>
                    <span className="text-right">Last Modified</span>
                    <span></span>
                  </div>
                  <div className="space-y-1">
                    {(form.credentials || []).map((c, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center text-xs px-2 py-1 rounded hover:bg-white" data-testid={`cred-row-${i}`}>
                        <span className="font-semibold truncate">{c.site}</span>
                        <span className="font-mono text-[#0A0A0A] truncate">{c.user_id}</span>
                        <span className="text-[10px] text-[#A1A1AA] text-right whitespace-nowrap">
                          {c.modified_at ? new Date(c.modified_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                        </span>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => copyCred(c.user_id)} title="Copy user id"
                            className="text-[#52525B] hover:text-[#3B82F6] p-1" data-testid={`cred-copy-${i}`}>
                            <Copy size={13} />
                          </button>
                          <button type="button" onClick={() => editCred(i)} title="Edit"
                            className="text-[#52525B] hover:text-[#3B82F6] p-1" data-testid={`cred-edit-${i}`}>
                            <PencilSimple size={13} />
                          </button>
                          <button type="button" onClick={() => removeCred(i)} title="Delete"
                            className="text-[#EF4444] hover:text-[#DC2626] p-1" data-testid={`cred-delete-${i}`}>
                            <Trash size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {editCustomer && isAdmin && (
              <div className="rounded-md border border-[#FDE68A]/70 bg-[#FFFBEB]/40 p-3 space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#92400E]">Advance Balance (\u20B9)</Label>
                <Input
                  type="number"
                  value={form.advance_balance}
                  onChange={e => setForm({ ...form, advance_balance: e.target.value })}
                  className="rounded-sm bg-white"
                  placeholder="0"
                  data-testid="customer-advance-balance-input"
                />
                <p className="text-[10px] text-[#92400E] italic">
                  Direct override (admin only). The difference auto-creates a Customer Advance Received / Refund entry against your first cash account so the Balance Sheet stays in sync.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleAdd} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-customer-btn">
              {editCustomer ? 'Update' : 'Save'} Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog */}
      <Dialog open={!!ledger} onOpenChange={() => setLedger(null)}>
        <DialogContent className="rounded-xl border-[#E4E4E7] w-screen max-w-none h-screen sm:!max-w-[98vw] sm:!w-[98vw] sm:h-[96vh] p-6 sm:p-8 flex flex-col gap-4 overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-2xl">Customer Ledger &mdash; {ledger?.name}</DialogTitle>
            <DialogDescription>
              Billed: {fmt(ledgerData.reduce((s, e) => s + (e.debit || 0), 0))} &nbsp;|&nbsp;
              Received: {fmt(ledgerData.reduce((s, e) => s + (e.credit || 0), 0))} &nbsp;|&nbsp;
              Due: <span className={ledgerTotalDue > 0 ? 'text-[#EF4444] font-semibold' : 'text-[#10B981]'}>{fmt(ledgerTotalDue)}</span>
              {ledgerAdvanceHeld > 0 && (
                <> &nbsp;|&nbsp; Advance Held: <span className="text-[#3B82F6] font-semibold">{fmt(ledgerAdvanceHeld)}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          {/* Quick Actions — jump from the ledger straight into a new bill or
              a receipt for this customer; both pages route back here on
              save / cancel via the openLedger query param. */}
          {ledger && canEdit && (
            <div className="flex items-center gap-2 -mt-1 mb-2 shrink-0" data-testid="ledger-quick-actions">
              <Button size="sm" variant="outline"
                className="rounded-lg h-8 px-3 text-xs border-[#3B82F6] text-[#3B82F6] hover:bg-[#EFF6FF]"
                onClick={() => navigate(`/invoices/create?customer=${encodeURIComponent(ledger.id)}&return_to_ledger=1`)}
                data-testid="ledger-create-invoice-btn">
                <FilePlus size={14} className="mr-1" /> Create Invoice
              </Button>
              <Button size="sm" variant="outline"
                className="rounded-lg h-8 px-3 text-xs border-[#10B981] text-[#10B981] hover:bg-[#ECFDF5]"
                onClick={() => navigate(`/receipt?customer=${encodeURIComponent(ledger.id)}&return_to_ledger=1`)}
                data-testid="ledger-receipt-btn">
                <ReceiptIcon size={14} className="mr-1" /> Receipt
              </Button>
            </div>
          )}
          {ledgerData.length === 0 ? <p className="text-sm text-[#52525B]">No transactions</p> : (
            <div className="flex-1 overflow-auto rounded-lg border border-[#E4E4E7]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Debit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Credit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Balance</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-center w-12">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Opening balance row — first thing in the period view. */}
                  <TableRow className="bg-[#F4F4F5] font-semibold">
                    <TableCell className="text-xs text-[#52525B]">{ledgerData[0]?.date ? new Date(ledgerData[0].date).toLocaleDateString('en-IN') : '-'}</TableCell>
                    <TableCell className="text-xs text-[#52525B] uppercase tracking-[0.15em]" colSpan={1}>Opening Balance</TableCell>
                    <TableCell className="text-xs text-right font-mono">-</TableCell>
                    <TableCell className="text-xs text-right font-mono">-</TableCell>
                    <TableCell className="text-xs text-right font-mono text-[#52525B]" data-testid="ledger-opening-balance">{fmt(0)}</TableCell>
                    <TableCell />
                  </TableRow>
                  {ledgerData.map((entry, i) => {
                    const isAdvance = entry.type === 'advance_received' || entry.type === 'advance_used' || entry.type === 'advance_refund';
                    const isDraft = entry.type === 'draft';
                    const rowClass = entry.type === 'payment' ? 'bg-[#F0FDF4]' : isAdvance ? 'bg-[#EFF6FF]' : isDraft ? 'bg-[#FEF3C7]' : '';
                    return (
                    <TableRow key={i} className={rowClass}>
                      <TableCell className="text-xs text-[#52525B]">{new Date(entry.date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell className="text-xs">
                        {entry.type === 'invoice' ? (
                          <span><span className="font-mono text-[#3B82F6]">{entry.invoice_number}</span> &mdash; {entry.description.split(' - ').slice(1).join(' - ') || 'Invoice'}</span>
                        ) : entry.type === 'payment' ? (
                          <span className="text-[#10B981]">{entry.receipt_number ? <span className="font-mono text-[#059669] font-semibold mr-1">{entry.receipt_number}</span> : null}Payment for {entry.invoice_number}</span>
                        ) : isDraft ? (
                          // Draft rows surface as an amber "DRAFT" pill + the
                          // service line; they're contributing to the running
                          // balance but haven't been billed yet.
                          <span className="text-[#92400E]">
                            <span className="px-1.5 py-0.5 rounded bg-[#F59E0B]/20 text-[#92400E] text-[10px] font-semibold mr-1.5 uppercase tracking-wide">Draft</span>
                            {entry.description?.replace(/^Draft - /, '') || 'Draft invoice'}
                            {entry.reason && <span className="text-[10px] text-[#A16207] italic ml-2">- {entry.reason}</span>}
                          </span>
                        ) : (
                          <span className="text-[#3B82F6]">{entry.invoice_number ? <span className="font-mono text-[#3B82F6] font-semibold mr-1">{entry.invoice_number}</span> : <span className="px-1.5 py-0.5 rounded bg-[#3B82F6]/10 text-[10px] font-semibold mr-1.5">RECEIPT</span>}{entry.description || (entry.type === 'advance_received' ? 'Advance received' : 'Advance applied')}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-right font-mono">{(entry.debit || 0) > 0 ? fmt(entry.debit) : (entry.advance_out > 0 ? <span className="text-[#3B82F6]">{fmt(entry.advance_out)}</span> : '')}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-[#10B981]">{(entry.credit || 0) > 0 ? fmt(entry.credit) : (entry.advance_in > 0 ? <span className="text-[#3B82F6]">{fmt(entry.advance_in)}</span> : '')}</TableCell>
                      <TableCell className={`text-xs text-right font-mono font-semibold ${entry.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(entry.balance || 0)}</TableCell>
                      <TableCell className="text-center">
                        {canEdit && entry.type === 'invoice' && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#3B82F6]"
                            onClick={() => { setLedger(null); navigate(`/invoices/edit/${entry.invoice_id}`); }}
                            title="Edit invoice"
                            data-testid={`edit-inv-${entry.invoice_id}`}>
                            <PencilSimple size={13} />
                          </Button>
                        )}
                        {canEdit && isDraft && entry.draft_id && (
                          // Opening a draft sends the user back to /invoices/create
                          // with the draft pre-loaded — same flow used everywhere
                          // else (MyWork, Drafts page).
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#F59E0B]"
                            onClick={() => { setLedger(null); navigate(`/invoices/create?draft=${entry.draft_id}`); }}
                            title="Open draft"
                            data-testid={`open-draft-${entry.draft_id}`}>
                            <PencilSimple size={13} />
                          </Button>
                        )}
                        {canEdit && entry.type === 'payment' && entry.receipt_number && (
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-[#52525B] hover:text-[#10B981]"
                            onClick={async () => {
                              try {
                                const { data: invData } = await api.get(`/invoices/${entry.invoice_id}`);
                                setEditingInvoice(invData);
                                setEditingReceipt({
                                  receipt_number: entry.receipt_number,
                                  amount: entry.credit,
                                  payment_mode: entry.payment_mode,
                                  date: entry.date,
                                });
                              } catch {
                                toast.error('Unable to load invoice for this receipt');
                              }
                            }}
                            title={`Edit ${entry.receipt_number}`}
                            data-testid={`edit-rcpt-${entry.receipt_number}`}>
                            <PencilSimple size={13} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {/* Closing balance row — running totals at the bottom. */}
                  {(() => {
                    const last = ledgerData[ledgerData.length - 1];
                    const closing = last?.balance ?? 0;
                    const totalDebit = ledgerData.reduce((s, e) => s + (e.debit || 0), 0);
                    const totalCredit = ledgerData.reduce((s, e) => s + (e.credit || 0), 0);
                    return (
                      <TableRow className="bg-[#F4F4F5] font-semibold border-t-2 border-[#0A0A0A]">
                        <TableCell className="text-xs text-[#52525B]">{last?.date ? new Date(last.date).toLocaleDateString('en-IN') : '-'}</TableCell>
                        <TableCell className="text-xs text-[#52525B] uppercase tracking-[0.15em]">Closing Balance</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmt(totalDebit)}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-[#10B981]">{fmt(totalCredit)}</TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${closing > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`} data-testid="ledger-closing-balance">{fmt(closing)}</TableCell>
                        <TableCell />
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          )}
          {ledgerTotalDue > 0 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E4E4E7] shrink-0">
              <div className="flex items-center gap-3">
                <p className="text-sm font-semibold text-[#52525B]">Total Due:</p>
                <p className="text-lg font-bold font-mono text-[#EF4444]">{fmt(ledgerTotalDue)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-lg px-4 font-semibold"
                  onClick={() => {
                    const unpaid = ledgerData.find(e => e.type === 'invoice' && e.balance > 0);
                    if (unpaid) { setShowPayDue(unpaid); setDuePayAmount(String(ledgerTotalDue)); setDuePayMode(finAccounts.find(a => a.type === 'cash' || a.type === 'bank')?.id || ''); }
                  }}
                  data-testid="ledger-receive-total-btn">
                  Receive {fmt(ledgerTotalDue)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive Due Dialog */}
      <Dialog open={!!showPayDue} onOpenChange={() => setShowPayDue(null)}>
        <DialogContent className="rounded-xl border-[#E4E4E7]">
          <DialogHeader>
            <DialogTitle>Receive Payment</DialogTitle>
            <DialogDescription>Collect due for {showPayDue?.invoice_number} &mdash; Due: {fmt(showPayDue?.balance || 0)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date</Label><Input type="date" value={duePayDate} onChange={e => setDuePayDate(e.target.value)} className="rounded-lg mt-1" data-testid="cust-due-pay-date" /></div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount</Label><Input type="number" value={duePayAmount} onChange={e => setDuePayAmount(e.target.value)} className="rounded-lg mt-1" data-testid="cust-due-pay-amount" /></div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Payment Mode</Label>
              <Select value={duePayMode} onValueChange={setDuePayMode}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="cust-due-pay-mode"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{finAccounts.filter(a => a.type === 'cash' || a.type === 'bank').map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={handleReceiveDue} className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-xl" data-testid="cust-confirm-due-payment">Receive Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Advance Dialog */}
      <Dialog open={!!showAdvance} onOpenChange={(v) => { if (!v) setShowAdvance(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7]">
          <DialogHeader>
            <DialogTitle>Receive Advance</DialogTitle>
            <DialogDescription>From {showAdvance?.name} &mdash; held as customer credit on the ledger.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Date</Label><Input type="date" value={advForm.date} onChange={e => setAdvForm({ ...advForm, date: e.target.value })} className="rounded-lg mt-1" data-testid="adv-date" /></div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Amount *</Label><Input type="number" value={advForm.amount} onChange={e => setAdvForm({ ...advForm, amount: e.target.value })} placeholder="0" className="rounded-lg mt-1" data-testid="adv-amount" /></div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Payment Mode *</Label>
              <Select value={advForm.payment_mode} onValueChange={v => setAdvForm({ ...advForm, payment_mode: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="adv-mode"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{finAccounts.filter(a => a.type === 'cash' || a.type === 'bank').map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Notes</Label><Input value={advForm.notes} onChange={e => setAdvForm({ ...advForm, notes: e.target.value })} placeholder="Optional" className="rounded-lg mt-1" data-testid="adv-notes" /></div>
          </div>
          <DialogFooter><Button onClick={handleReceiveAdvance} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="adv-confirm-btn">Receive Advance</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-semibold text-[#0A0A0A]">{confirmDelete?.name}</span>. Allowed only when the customer has no invoices and no ledger transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCustomer} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="delete-customer-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit payment receipt — opened from ledger pencil icons */}
      <PaymentReceiptDialog
        open={!!editingReceipt}
        onOpenChange={(o) => { if (!o) { setEditingReceipt(null); setEditingInvoice(null); } }}
        invoice={editingInvoice}
        receipt={editingReceipt}
        accounts={finAccounts}
        onSaved={async () => {
          // Refresh the currently open customer ledger so the new amounts appear.
          if (ledger) {
            try {
              const { data } = await api.get(`/customers/${ledger.id}/ledger`);
              setLedgerData(data);
            } catch { /* no-op */ }
          }
          fetchCustomers();
        }}
      />
    </div>
  );
}
